import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { FieldError } from '@/components/ui/field-error';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { eventFormClasses } from '@/lib/utils/events.utils';
import {
  generateRecurrenceDates,
  formatDatesForPreview,
} from '@/lib/utils/recurrence.utils';
import type {
  RecurrenceType,
  RecurrenceSelectorProps,
} from '@/lib/types/events.types';
import { WarningCircleIcon } from '@phosphor-icons/react';

const RECURRENCE_OPTIONS: { value: RecurrenceType; label: string }[] = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly (same date)' },
];

/**
 * Presentational recurrence selector component for event form
 * Allows users to select recurrence type and number of occurrences
 */
export function RecurrenceSelector({
  disabled = false,
  recurrenceType,
  recurrenceCount,
  eventDate,
  startTime,
  onRecurrenceTypeChange,
  onRecurrenceCountChange,
  onRecurrenceTypeBlur,
  onRecurrenceCountBlur,
  recurrenceTypeErrors = [],
  recurrenceCountErrors = [],
  showErrors = false,
}: RecurrenceSelectorProps) {
  // Generate preview dates when recurrence is enabled
  const previewDates = useMemo(() => {
    if (
      recurrenceType === 'none' ||
      !eventDate ||
      !startTime ||
      recurrenceCount < 2
    ) {
      return [];
    }

    try {
      const startDateTime = new Date(`${eventDate}T${startTime}`);
      const dates = generateRecurrenceDates(
        startDateTime,
        recurrenceType,
        recurrenceCount
      );
      return formatDatesForPreview(dates);
    } catch {
      return [];
    }
  }, [recurrenceType, eventDate, startTime, recurrenceCount]);

  const hasRecurrenceTypeErrors = recurrenceTypeErrors.length > 0;
  const hasRecurrenceCountErrors = recurrenceCountErrors.length > 0;
  const isRecurrenceCountInvalid = showErrors && hasRecurrenceCountErrors;

  const isRecurrenceTypeInvalid = showErrors && hasRecurrenceTypeErrors;

  return (
    <div className="space-y-4">
      <label className={eventFormClasses.label}>Recurring Event?</label>

      {disabled ? (
        /* Callout when recurrence is disabled */
        <div className="bg-slate-700/50 border border-red-400 rounded-lg p-3">
          <p className="text-body text-red-400 flex flex-row items-center gap-2">
            <WarningCircleIcon size={24} />
            Recurrence is only avilable for new events.
          </p>
        </div>
      ) : (
        /* Recurrence type selection */
        <div className="space-y-2">
          <Select
            value={recurrenceType}
            onValueChange={(value) =>
              onRecurrenceTypeChange(value as RecurrenceType)
            }
            onOpenChange={(open) => {
              if (!open && onRecurrenceTypeBlur) {
                onRecurrenceTypeBlur();
              }
            }}
          >
            <SelectTrigger
              className={`${eventFormClasses.input} ${isRecurrenceTypeInvalid ? 'border-red-500' : ''}`}
              aria-invalid={isRecurrenceTypeInvalid || undefined}
            >
              <SelectValue placeholder="Select recurrence type" />
            </SelectTrigger>
            <SelectContent>
              {RECURRENCE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showErrors && hasRecurrenceTypeErrors && (
            <FieldError errors={recurrenceTypeErrors} />
          )}
        </div>
      )}

      {/* Recurrence count input (only show when recurrence is enabled and not disabled) */}
      {!disabled && recurrenceType !== 'none' && (
        <div className="space-y-2">
          <label className="text-body text-slate-50" htmlFor="recurrenceCount">
            Number of occurrences
          </label>
          <Input
            id="recurrenceCount"
            name="recurrenceCount"
            type="number"
            min={2}
            max={52}
            value={recurrenceCount ?? 2}
            onChange={(event) =>
              onRecurrenceCountChange(parseInt(event.target.value, 10) || 2)
            }
            onBlur={onRecurrenceCountBlur}
            aria-invalid={isRecurrenceCountInvalid || undefined}
            className={`${eventFormClasses.input} ${isRecurrenceCountInvalid ? 'border-red-500' : ''}`}
          />
          {showErrors && hasRecurrenceCountErrors && (
            <FieldError errors={recurrenceCountErrors} />
          )}
        </div>
      )}

      {/* Preview dates (only show when recurrence is enabled and not disabled) */}
      {!disabled && recurrenceType !== 'none' && previewDates.length > 0 && (
        <div className="space-y-2">
          <p className="text-body text-slate-50 font-semibold">Preview:</p>
          <div className="bg-slate-700/50 rounded-lg p-4 space-y-1">
            {previewDates.map((date, index) => (
              <p key={index} className="text-body text-slate-300">
                • {date}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

